<template>
  <div class="reply-box" :class="replyBoxClass">
    <banner
      v-if="showSelfAssignBanner"
      color-scheme="secondary"
      :banner-message="$t('CONVERSATION.NOT_ASSIGNED_TO_YOU')"
      :has-action-button="true"
      :action-button-label="$t('CONVERSATION.ASSIGN_TO_ME')"
      @click="onClickSelfAssign"
    />
    <reply-top-panel
      :mode="replyType"
      :set-reply-mode="setReplyMode"
      :is-message-length-reaching-threshold="isMessageLengthReachingThreshold"
      :characters-remaining="charactersRemaining"
      :popout-reply-box="popoutReplyBox"
      :show-reply-button="showReplyTo"
      @click="$emit('click')"
    />
    <div class="reply-box__top">
      <ReplyToMessage
        v-if="shouldShowReplyToMessage"
        :message="inReplyTo"
        @dismiss="resetReplyToMessage"
      />
      <canned-response
        v-if="showMentions && hasSlashCommand"
        v-on-clickaway="hideMentions"
        class="normal-editor__canned-box"
        :search-key="mentionSearchKey"
        @click="replaceText"
      />
      <emoji-input
        v-if="showEmojiPicker"
        v-on-clickaway="hideEmojiPicker"
        :class="emojiDialogClassOnExpanedLayout"
        :on-click="emojiOnClick"
      />
      <reply-email-head
        v-if="showReplyHead"
        :cc-emails.sync="ccEmails"
        :bcc-emails.sync="bccEmails"
      />
      <woot-audio-recorder
        v-if="showAudioRecorderEditor"
        ref="audioRecorderInput"
        :audio-record-format="audioRecordFormat"
        @state-recorder-progress-changed="onStateProgressRecorderChanged"
        @state-recorder-changed="onStateRecorderChanged"
        @finish-record="onFinishRecorder"
      />
      <resizable-text-area
        v-else-if="!showRichContentEditor"
        ref="messageInput"
        v-model="message"
        class="input"
        :placeholder="messagePlaceHolder"
        :min-height="4"
        :readonly="!canReply"
        @typing-off="onTypingOff"
        @typing-on="onTypingOn"
        @focus="onFocus"
        @blur="onBlur"
      />
      <woot-message-editor
        v-else
        v-model="message"
        :editor-id="editorStateId"
        class="input"
        :is-private="isOnPrivateNote"
        :placeholder="messagePlaceHolder"
        :update-selection-with="updateEditorSelectionWith"
        :min-height="4"
        :enable-variables="true"
        :variables="messageVariables"
        @typing-off="onTypingOff"
        @typing-on="onTypingOn"
        @focus="onFocus"
        @blur="onBlur"
        @toggle-user-mention="toggleUserMention"
        @toggle-canned-menu="toggleCannedMenu"
        @toggle-variables-menu="toggleVariablesMenu"
        @clear-selection="clearEditorSelection"
      />
    </div>
    <div v-if="hasAttachments" class="attachment-preview-box" @paste="onPaste">
      <attachment-preview
        :attachments="attachedFiles"
        :remove-attachment="removeAttachment"
        :remove-attachment-evolution-api="removeAttachmentEvolutionApi"
        :remove-audios-attachments="removeAudiosAttachments"
      />
    </div>
    <div
      v-if="isSignatureEnabledForInbox"
      v-tooltip="$t('CONVERSATION.FOOTER.MESSAGE_SIGN_TOOLTIP')"
      class="message-signature-wrap"
    >
      <p
        v-if="isSignatureAvailable"
        v-dompurify-html="formatMessage(messageSignature)"
        class="message-signature"
      />
      <p v-else class="message-signature">
        {{ $t('CONVERSATION.FOOTER.MESSAGE_SIGNATURE_NOT_CONFIGURED') }}
        <router-link :to="profilePath">
          {{ $t('CONVERSATION.FOOTER.CLICK_HERE') }}
        </router-link>
      </p>
    </div>
    <reply-bottom-panel
      :conversation-id="conversationId"
      :enable-multiple-file-upload="enableMultipleFileUpload"
      :show-whatsapp-button-templates="showWhatsappButtonTemplates"
      :whatsapp-templates-disabled="whatsappTemplatesDisabled"
      :show-messenger-button-templates="showMessengerButtonTemplates"
      :messenger-templates-disabled="messengerTemplatesDisabled"
      :inbox="inbox"
      :is-on-private-note="isOnPrivateNote"
      :is-recording-audio="isRecordingAudio"
      :is-send-disabled="isReplyButtonDisabled"
      :can-reply="canReply"
      :mode="replyType"
      :on-file-upload="onFileUpload"
      :on-send="onSendReply"
      :recording-audio-duration-text="recordingAudioDurationText"
      :recording-audio-state="recordingAudioState"
      :send-button-text="replyButtonLabel"
      :show-audio-recorder="showAudioRecorder"
      :is-audio-recorder-disabled="isAudioRecorderDisabled"
      :show-editor-toggle="isAPIInbox && !isOnPrivateNote"
      :show-emoji-picker="showEmojiPicker"
      :show-file-upload="showFileUpload"
      :toggle-audio-recorder-play-pause="toggleAudioRecorderPlayPause"
      :toggle-audio-recorder="toggleAudioRecorder"
      :toggle-emoji-picker="toggleEmojiPicker"
      @selectWhatsappTemplate="openWhatsappTemplateModal"
      @selectMessengerTemplate="openMessengerTemplateModal"
      @toggle-editor="toggleRichContentEditor"
    />
    <whatsapp-templates
      :inbox-id="inbox.id"
      :show="showWhatsAppTemplatesModal"
      @close="hideWhatsappTemplatesModal"
      @on-send="onSendWhatsAppReply"
      @cancel="hideWhatsappTemplatesModal"
    />
    <messenger-templates
      :inbox-id="inbox.id"
      :show="showMessengerTemplatesModal"
      @on-send="onSendMessengerReply"
      @cancel="hideMessengerTemplatesModal"
    />

    <woot-confirm-modal
      ref="confirmDialog"
      :title="$t('CONVERSATION.REPLYBOX.UNDEFINED_VARIABLES.TITLE')"
      :description="undefinedVariableMessage"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { mixin as clickaway } from 'vue-clickaway';
import alertMixin from 'shared/mixins/alertMixin';

import CannedResponse from './CannedResponse';
import ReplyToMessage from './ReplyToMessage.vue';
import ResizableTextArea from 'shared/components/ResizableTextArea';
import AttachmentPreview from 'dashboard/components/widgets/AttachmentsPreview';
import ReplyTopPanel from 'dashboard/components/widgets/WootWriter/ReplyTopPanel';
import ReplyEmailHead from './ReplyEmailHead';
import ReplyBottomPanel from 'dashboard/components/widgets/WootWriter/ReplyBottomPanel';
import Banner from 'dashboard/components/ui/Banner.vue';
import { REPLY_EDITOR_MODES } from 'dashboard/components/widgets/WootWriter/constants';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor';
import WootAudioRecorder from 'dashboard/components/widgets/WootWriter/AudioRecorder';
import messageFormatterMixin from 'shared/mixins/messageFormatterMixin';
import { checkFileSizeLimit } from 'shared/helpers/FileHelper';
import {
  MAXIMUM_FILE_UPLOAD_SIZE_TWILIO_SMS_CHANNEL,
  AUDIO_FORMATS,
  getSizeFile,
} from 'shared/constants/messages';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { replaceVariablesInMessage } from 'dashboard/helper/messageHelper';
import {
  getMessageVariables,
  getUndefinedVariablesInMessage,
} from 'dashboard/helper/messageHelper';
import WhatsappTemplates from './WhatsappTemplates/Modal.vue';
import MessengerTemplates from './MessengerTemplates/Modal.vue';
import { buildHotKeys } from 'shared/helpers/KeyboardHelpers';
import { MESSAGE_MAX_LENGTH } from 'shared/helpers/MessageTypeHelper';
import {
  shouldShowMessengerButtonTemplates,
  shouldRefreshMessengerTemplates,
} from 'dashboard/helper/messengerTemplateHelper';
import inboxMixin, { INBOX_TYPES } from 'shared/mixins/inboxMixin';
import uiSettingsMixin from 'dashboard/mixins/uiSettings';
import { DirectUpload } from 'activestorage';
import { frontendURL } from '../../../helper/URLHelper';
import { LocalStorage, LOCAL_STORAGE_KEYS } from '../../../helper/localStorage';
import { trimContent, debounce } from '@chatwoot/utils';
import wootConstants from 'dashboard/constants';
import { isEditorHotKeyEnabled } from 'dashboard/mixins/uiSettings';
import { CONVERSATION_EVENTS } from '../../../helper/AnalyticsHelper/events';

const EmojiInput = () => import('shared/components/emoji/EmojiInput');

export default {
  components: {
    EmojiInput,
    CannedResponse,
    ResizableTextArea,
    AttachmentPreview,
    ReplyTopPanel,
    ReplyEmailHead,
    ReplyToMessage,
    ReplyBottomPanel,
    WootMessageEditor,
    WootAudioRecorder,
    Banner,
    WhatsappTemplates,
    MessengerTemplates,
  },
  mixins: [
    clickaway,
    inboxMixin,
    uiSettingsMixin,
    alertMixin,
    messageFormatterMixin,
  ],
  props: {
    selectedTweet: {
      type: [Object, String],
      default: () => ({}),
    },
    isATweet: {
      type: Boolean,
      default: false,
    },
    popoutReplyBox: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      message: '',
      inReplyTo: {},
      isFocused: false,
      showEmojiPicker: false,
      attachedFiles: [],
      isRecordingAudio: false,
      recordingAudioState: '',
      recordingAudioDurationText: '',
      isUploading: false,
      replyType: REPLY_EDITOR_MODES.REPLY,
      mentionSearchKey: '',
      hasSlashCommand: false,
      bccEmails: '',
      ccEmails: '',
      doAutoSaveDraft: () => {},
      showWhatsAppTemplatesModal: false,
      showMessengerTemplatesModal: false,
      updateEditorSelectionWith: '',
      undefinedVariableMessage: '',
      showMentions: false,
      showUserMentions: false,
      showCannedMenu: false,
      showVariablesMenu: false,
      canReply: false,
      showReplyTo: false,
    };
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      messageSignature: 'getMessageSignature',
      currentUser: 'getCurrentUser',
      lastEmail: 'getLastEmailInSelectedChat',
      globalConfig: 'globalConfig/get',
      accountId: 'getCurrentAccountId',
      getConversationReplyType: 'getConversationReplyType',
      getDraft: 'getDraft',
      groupMembers: 'getMembers',
    }),
    // Retorna false si es Intertel UC, o usa allow_send_messages si es Evolution API WhatsApp.
    showReplyToTab() {
      if (this.isIntertelUCInbox) {
        return false;
      }
      if (this.isAnEvolutionApiWhatsAppChannel) {
        if (!this.inbox.linked) {
          return true;
        }

        return this.currentChat.can_reply;
      }
      return true;
    },
    /**
     * Determina si se debe mostrar el mensaje de respuesta basado en si hay un mensaje al que se responde y si el mensaje no es privado.
     *
     * @returns {boolean} Retorna `true` si se debe mostrar el mensaje de respuesta, `false` en caso contrario.
     */
    shouldShowReplyToMessage() {
      return this.inReplyTo?.id && !this.isPrivate;
    },
    showRichContentEditor() {
      if (this.isOnPrivateNote || this.isRichEditorEnabled) {
        return true;
      }

      if (this.isAPIInbox) {
        const {
          display_rich_content_editor: displayRichContentEditor = false,
        } = this.uiSettings;
        return displayRichContentEditor;
      }

      return false;
    },
    assignedAgent: {
      get() {
        return this.currentChat.meta.assignee;
      },
      set(agent) {
        const agentId = agent ? agent.id : 0;
        this.$store.dispatch('setCurrentChatAssignee', agent);
        this.$store
          .dispatch('assignAgent', {
            conversationId: this.currentChat.id,
            agentId,
          })
          .then(() => {
            this.showAlert(this.$t('CONVERSATION.CHANGE_AGENT'));
          });
      },
    },
    showSelfAssignBanner() {
      if (this.message !== '' && !this.isOnPrivateNote) {
        if (!this.assignedAgent) {
          return true;
        }
        if (this.assignedAgent.id !== this.currentUser.id) {
          return true;
        }
      }

      return false;
    },
    // Espejo de showMessengerButtonTemplates: en canal WhatsApp Cloud el botón nunca depende de
    // datos (el modal informa cuando no hay plantillas); en el resto de canales (API, Evolution
    // API, etc.) se muestra solo si se detectan plantillas en el store.
    showWhatsappButtonTemplates() {
      if (this.channelType === INBOX_TYPES.WHATSAPP) return true;
      return !!this.$store.getters['inboxes/getWhatsAppTemplates'](this.inboxId)
        .length;
    },
    // Con el token caducado el botón de plantillas se muestra pero deshabilitado:
    // el envío también fallaría con 401 hasta reautorizar el canal.
    whatsappTemplatesDisabled() {
      return Boolean(this.inbox.reauthorization_required);
    },
    messengerTemplatesDisabled() {
      return Boolean(this.inbox.reauthorization_required);
    },
    showMessengerButtonTemplates() {
      if (!this.globalConfig.enableFacebookTemplates) {
        return false;
      }
      return shouldShowMessengerButtonTemplates({
        isFacebookInbox: this.isAFacebookInbox,
        isInstagramDMConversation: this.isAnInstagramDMConversation,
      });
    },
    isAnInstagramDMConversation() {
      return (
        this.currentChat?.additional_attributes?.type ===
        'instagram_direct_message'
      );
    },
    isPrivate() {
      return this.isOnPrivateNote;
    },
    inboxId() {
      return this.currentChat.inbox_id;
    },
    inbox() {
      return this.$store.getters['inboxes/getInbox'](this.inboxId);
    },
    messagePlaceHolder() {
      if (this.isPrivate) {
        return this.$t('CONVERSATION.FOOTER.PRIVATE_MSG_INPUT');
      }
      if (this.canReply) {
        return this.$t('CONVERSATION.FOOTER.MSG_INPUT');
      }
      // Con el token caducado no aplica sugerir plantillas: también fallarían con 401.
      if (
        (this.isAWhatsAppChannel || this.isAFacebookInbox) &&
        this.inbox.reauthorization_required
      ) {
        return this.$t('CONVERSATION.CAN_REPLY_PLACEHOLDER');
      }
      if (this.isAWhatsAppCloudChannel) {
        return this.$t('CONVERSATION.CAN_REPLY_WHATSAPP_PLACEHOLDER');
      }
      if (this.isAFacebookInbox && !this.isAnInstagramDMConversation) {
        return this.$t('CONVERSATION.CAN_REPLY_MESSENGER_PLACEHOLDER');
      }
      return this.$t('CONVERSATION.CAN_REPLY_PLACEHOLDER');
    },
    isMessageLengthReachingThreshold() {
      return this.message.length > this.maxLength - 50;
    },
    charactersRemaining() {
      return this.maxLength - this.message.length;
    },
    isReplyButtonDisabled() {
      if (
        (!this.canReply && !this.isOnPrivateNote) ||
        (this.isATweet && !this.inReplyTo && !this.isOnPrivateNote)
      ) {
        return true;
      }

      if (this.hasAttachments || this.hasRecordedAudio) return false;

      return (
        this.isMessageEmpty ||
        this.message.length === 0 ||
        this.message.length > this.maxLength
      );
    },
    conversationType() {
      const { additional_attributes: additionalAttributes } = this.currentChat;
      const type = additionalAttributes ? additionalAttributes.type : '';
      return type || '';
    },
    maxLength() {
      if (this.isPrivate) {
        return MESSAGE_MAX_LENGTH.GENERAL;
      }
      if (this.isAFacebookInbox) {
        return MESSAGE_MAX_LENGTH.FACEBOOK;
      }
      if (this.isAWhatsAppChannel) {
        return MESSAGE_MAX_LENGTH.TWILIO_WHATSAPP;
      }
      if (this.isASmsInbox) {
        return MESSAGE_MAX_LENGTH.TWILIO_SMS;
      }
      if (this.isATwitterInbox) {
        if (this.conversationType === 'tweet') {
          return MESSAGE_MAX_LENGTH.TWEET - this.replyToUserLength - 2;
        }
      }
      return MESSAGE_MAX_LENGTH.GENERAL;
    },
    showFileUpload() {
      return (
        this.isAWebWidgetInbox ||
        this.isAFacebookInbox ||
        this.isAWhatsAppChannel ||
        this.isAPIInbox ||
        this.isAnEmailChannel ||
        this.isASmsInbox ||
        this.isATelegramChannel ||
        this.isAnEvolutionApiWhatsAppChannel
      );
    },
    replyButtonLabel() {
      let sendMessageText = this.$t('CONVERSATION.REPLYBOX.SEND');
      if (this.isPrivate) {
        sendMessageText = this.$t('CONVERSATION.REPLYBOX.CREATE');
      } else if (this.conversationType === 'tweet') {
        sendMessageText = this.$t('CONVERSATION.REPLYBOX.TWEET');
      }
      const keyLabel = isEditorHotKeyEnabled(this.uiSettings, 'cmd_enter')
        ? '(⌘ + ↵)'
        : '(↵)';
      return `${sendMessageText} ${keyLabel}`;
    },
    replyBoxClass() {
      return {
        'is-private': this.isPrivate,
        'is-focused': this.isFocused || this.hasAttachments,
      };
    },
    hasAttachments() {
      return this.attachedFiles.length;
    },
    hasRecordedAudio() {
      return (
        this.$refs.audioRecorderInput &&
        this.$refs.audioRecorderInput.hasAudio()
      );
    },
    isRichEditorEnabled() {
      return this.isAWebWidgetInbox || this.isAnEmailChannel;
    },
    showAudioRecorder() {
      return !this.isOnPrivateNote && this.showFileUpload;
    },
    // Regresa si se debe deshabilitar audio cuando es respuesta de mensaje y Evolution API
    isAudioRecorderDisabled() {
      return (
        this.shouldShowReplyToMessage && this.isAnEvolutionApiWhatsAppChannel
      );
    },
    // Regresa si se debe eliminar archivos de audio cuando es respuesta de mensaje y Evolution API
    removeAttachmentEvolutionApi() {
      return this.isAudioRecorderDisabled;
    },
    showAudioRecorderEditor() {
      return this.showAudioRecorder && this.isRecordingAudio;
    },
    isOnPrivateNote() {
      return this.replyType === REPLY_EDITOR_MODES.NOTE;
    },
    isOnExpandedLayout() {
      const {
        LAYOUT_TYPES: { CONDENSED },
      } = wootConstants;
      const {
        conversation_display_type: conversationDisplayType = CONDENSED,
      } = this.uiSettings;
      return conversationDisplayType !== CONDENSED;
    },
    emojiDialogClassOnExpanedLayout() {
      return this.isOnExpandedLayout || this.popoutReplyBox
        ? 'emoji-dialog--expanded'
        : '';
    },
    replyToUserLength() {
      const selectedTweet = this.selectedTweet || {};
      const {
        sender: {
          additional_attributes: { screen_name: screenName = '' } = {},
        } = {},
      } = selectedTweet;
      return screenName ? screenName.length : 0;
    },
    isMessageEmpty() {
      if (!this.message) {
        return true;
      }
      return !this.message.trim().replace(/\n/g, '').length;
    },
    showReplyHead() {
      return !this.isOnPrivateNote && this.isAnEmailChannel;
    },
    enableMultipleFileUpload() {
      return this.isAnEmailChannel || this.isAWebWidgetInbox || this.isAPIInbox;
    },
    isSignatureEnabledForInbox() {
      return !this.isPrivate && this.isAnEmailChannel && this.sendWithSignature;
    },
    isSignatureAvailable() {
      return !!this.messageSignature;
    },
    sendWithSignature() {
      const { send_with_signature: isEnabled } = this.uiSettings;
      return isEnabled;
    },
    profilePath() {
      return frontendURL(`accounts/${this.accountId}/profile/settings`);
    },
    editorMessageKey() {
      const { editor_message_key: isEnabled } = this.uiSettings;
      return isEnabled;
    },
    commandPlusEnterToSendEnabled() {
      return this.editorMessageKey === 'cmd_enter';
    },
    enterToSendEnabled() {
      return this.editorMessageKey === 'enter';
    },
    conversationId() {
      return this.currentChat.id;
    },
    conversationIdByRoute() {
      return this.conversationId;
    },
    editorStateId() {
      return `draft-${this.conversationIdByRoute}-${this.replyType}`;
    },
    audioRecordFormat() {
      if (this.isAWebWidgetInbox) {
        return AUDIO_FORMATS.WEBM;
      }
      return AUDIO_FORMATS.OGG;
    },
    messageVariables() {
      const variables = getMessageVariables({
        conversation: this.currentChat,
      });
      return variables;
    },
  },
  watch: {
    // Observa los cambios en el estado de vinculación del buzón de entrada de evolution api.
    'inbox.linked'() {
      this.setCanReply();
    },
    /**
     * Actualiza el estado de la conversación actual.
     * @param {Object} conversation - Objeto de la conversación actual.
     */
    currentChat(conversation, oldConversation) {
      if (conversation.id !== oldConversation.id) {
        this.resetReplyToMessage();
      }

      this.initReplyState();
    },
    // Detecta si hubo un cambio de estado en can_reply
    'currentChat.can_reply'() {
      this.setCanReply();
      this.showReplyTo = this.showReplyToTab;
    },
    // Recalcula el composer cuando cambia en vivo la reautorización del canal (token caducado).
    'inbox.reauthorization_required'() {
      this.setCanReply();
    },
    conversationIdByRoute(conversationId, oldConversationId) {
      if (conversationId !== oldConversationId) {
        this.getFromDraft();
      }
    },
    // Fallback: al montar, currentChat.messages puede no tener aún el histórico (fetch async),
    // así que reintenta la cita cuando cambia la cantidad de mensajes cargados. El guard por
    // inReplyTo ya resuelto (en restoreReplyToMessage) evita reintentos en cada mensaje nuevo.
    'currentChat.messages.length'(newLength, oldLength) {
      if (newLength > (oldLength ?? 0)) {
        this.restoreReplyToMessage();
      }
    },
    message(updatedMessage) {
      this.hasSlashCommand =
        updatedMessage[0] === '/' && !this.showRichContentEditor;
      const hasNextWord = updatedMessage.includes(' ');
      const isShortCodeActive = this.hasSlashCommand && !hasNextWord;
      if (isShortCodeActive) {
        this.mentionSearchKey = updatedMessage.substring(1);
        this.showMentions = true;
      } else {
        this.mentionSearchKey = '';
        this.showMentions = false;
        this.showCannedMenu = false;
      }
      this.doAutoSaveDraft();
    },
    replyType(updatedReplyType, oldReplyType) {
      this.getFromDraft();
    },
    /**
     * Actualiza el estado de los miembros del grupo.
     */
    groupMembers() {
      this.setCanReply();
    },
  },

  created() {
    // Inboxes de Facebook para los que ya se intentó refrescar las plantillas (evita refetchs
    // repetidos). Es estado NO reactivo (solo bookkeeping, no se renderiza): se declara aquí y no
    // en data() para que Vue no lo observe. Vive en el ciclo de vida del componente (se reinicia
    // al recargar). No necesita purga: un inbox recreado tiene id nuevo (se refresca igual), el
    // broadcast en tiempo real (inbox.templates_updated) actualiza el store sin pasar por este Set,
    // y un cambio de cuenta desmonta el componente (lo resetea).
    this.refreshedTemplateInboxes = new Set();
  },

  mounted() {
    // initReplyState reinicia el estado de respuesta en el (re)montaje: al cambiar de tab en ConversationBox.vue el ReplyBox se recrea con la
    // misma conversación, y sin esto canReply quedaría en false (default) y se oculta el área de texto donde el agente responde al contacto.
    this.initReplyState();
    this.getFromDraft();
    // Al (re)montar (p. ej. al volver a la pestaña de mensajes en ConversationBox.vue, que destruye
    // y recrea el ReplyBox al cambiar de tab) restaura la referencia de respuesta desde localStorage
    // si sigue vigente para esta conversación, para que el mensaje citado no se pierda del área de texto.
    this.restoreReplyToMessage();
    // Donot use the keyboard listener mixin here as the events here are supposed to be
    // working even if input/textarea is focussed.
    document.addEventListener('paste', this.onPaste);
    document.addEventListener('keydown', this.handleKeyEvents);
    this.doAutoSaveDraft = debounce(
      () => {
        this.saveDraft(this.conversationIdByRoute, this.replyType);
      },
      500,
      true
    );
    bus.$on(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE, this.fetchAndSetReplyTo);
  },
  destroyed() {
    document.removeEventListener('paste', this.onPaste);
    document.removeEventListener('keydown', this.handleKeyEvents);
    bus.$off(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE, this.fetchAndSetReplyTo);
  },
  methods: {
    ...mapActions(['updateConversationReplyType']),

    /**
     * Fallback al broadcast en tiempo real: si esta es una conversación de Facebook (no Instagram DM)
     * cuyo inbox aún no tiene plantillas en el store (p. ej. el inbox se acaba de crear y el evento
     * inbox.templates_updated se emitió antes de estar suscrito), refresca los inboxes una sola vez
     * para que el botón de plantillas aparezca sin recargar. Gateado a Facebook: no afecta otros canales.
     */
    ensureMessengerTemplatesLoaded() {
      const { inboxId } = this;
      if (!inboxId) return;
      // templatesLoaded = el record ya trae message_templates (incluye "[]" sincronizado vacío);
      // null/undefined = nunca sincronizado → es el único caso en que el fallback debe consultar.
      const inbox = this.$store.getters['inboxes/getInbox'](inboxId);
      const shouldRefresh = shouldRefreshMessengerTemplates({
        isFacebookInbox: this.isAFacebookInbox,
        isInstagramDMConversation: this.isAnInstagramDMConversation,
        alreadyRefreshed: this.refreshedTemplateInboxes.has(inboxId),
        templatesLoaded: inbox?.message_templates != null,
      });
      if (!shouldRefresh) return;
      // Se marca ANTES del dispatch (síncrono): una segunda apertura del mismo inbox verá
      // alreadyRefreshed=true y no hará otro fetch, aun si la 1ª respuesta no ha vuelto. Así se
      // evitan fetches/commits duplicados del mismo inbox ante cambios rápidos de conversación
      // (el commit además es idempotente).
      this.refreshedTemplateInboxes.add(inboxId);
      // Refresca solo este inbox (no todos): trae sus plantillas vía GET /inboxes/:id.
      this.$store.dispatch('inboxes/fetchInbox', inboxId);
    },

    /**
     * Fuente única de inicialización del estado de respuesta. La invocan tanto mounted() (cubre el
     * (re)montaje al cambiar de tab) como el watcher currentChat (cubre el cambio de conversación),
     * para evitar que ambos diverjan. Lo propio de cada uno queda fuera: getFromDraft/listeners en
     * mounted; resetReplyToMessage condicional por cambio de id en el watcher.
     */
    initReplyState() {
      this.setCanReply();
      this.showReplyTo = this.showReplyToTab;
      // Intertel UC: forzar Nota Privada (setInitialReplyType también lo respeta vía setReplyType).
      if (this.isIntertelUCInbox) {
        this.setReplyMode(REPLY_EDITOR_MODES.NOTE);
      }
      this.setInitialReplyType();
      this.setCCEmailFromLastChat();
      this.ensureMessengerTemplatesLoaded();
    },

    /**
     * Método para establecer si es posible responder a la conversación actual.
     */
    setCanReply() {
      this.canReply = this.currentChat.can_reply;
      if (this.isAnEvolutionApiWhatsAppChannel && this.canReply) {
        this.canReply = this.isCanReplyEvolutionApiChannel;
      }
      // Token caducado: el flag inbox-level llega en vivo (inbox.reauthorization_changed),
      // así el composer se bloquea sin depender del evento por conversación.
      if (
        (this.isAWhatsAppChannel || this.isAFacebookInbox) &&
        this.inbox.reauthorization_required
      ) {
        this.canReply = false;
      }

      this.setCanReplyByGroupMember();
    },

    /**
     * Método para establecer si se puede responder la conversación actual.
     */
    setCanReplyByGroupMember() {
      if (this.isAnEvolutionApiWhatsAppChannel && this.currentChat.is_group) {
        if (!this.canReply || this.groupMembers.length === 0) {
          // Si no se puede responder, no hay necesidad de verificar los miembros del grupo.
          return;
        }

        this.canReply = this.groupMembers.some(
          member =>
            member.phone_number ===
            this.normalizedPhoneNumber(this.inbox.phone_number)
        );
      }
    },
    /**
     * Método para establecer el tipo de respuesta inicial de la conversación.
     */
    setInitialReplyType() {
      const conversationId = this.currentChat.id;
      const lastReplyType = this.getConversationReplyType(conversationId);

      if (lastReplyType) {
        if (
          this.isAnEvolutionApiWhatsAppChannel &&
          !this.currentChat.can_reply
        ) {
          this.setReplyType(REPLY_EDITOR_MODES.NOTE);
          return;
        }

        this.setReplyMode(lastReplyType);
      } else {
        let type = REPLY_EDITOR_MODES.NOTE;

        if (this.isAnEvolutionApiWhatsAppChannel) {
          if (this.isCanReplyEvolutionApiChannel && this.currentChat.can_reply)
            type = REPLY_EDITOR_MODES.REPLY;
        } else {
          type =
            this.currentChat.can_reply || this.isAWhatsAppChannel
              ? REPLY_EDITOR_MODES.REPLY
              : REPLY_EDITOR_MODES.NOTE;
        }

        this.setReplyMode(type);
      }
    },

    /**
     * Método para establecer el tipo de respuesta de la conversación.
     * @param {string} type - Tipo de respuesta a establecer.
     */
    setReplyType(type) {
      if (this.isIntertelUCInbox) type = REPLY_EDITOR_MODES.NOTE;
      const conversationId = this.currentChat.id;
      this.updateConversationReplyType({
        conversationId,
        replyType: type,
      });
      this.replyType = type;
    },
    toggleRichContentEditor() {
      this.updateUISettings({
        display_rich_content_editor: !this.showRichContentEditor,
      });
    },
    /**
     * Guarda el borrador actual del mensaje en el store de Vuex.
     *
     * @param {string} conversationId - El ID de la conversación.
     * @param {string} replyType - El tipo de respuesta (p. ej., 'responder', 'nota privada').
     */
    saveDraft(conversationId, replyType) {
      if (this.message || this.message === '') {
        const draftToSave = {
          message: trimContent(this.message || ''),
          attachedFiles: this.attachedFiles,
        };

        this.$store.dispatch('saveDraft', {
          conversationId,
          replyType,
          draft: draftToSave,
        });
      }
    },
    setToDraft(conversationId, replyType) {
      this.saveDraft(conversationId, replyType);
      this.message = '';
    },
    /**
     * Obtiene el borrador del mensaje guardado para la conversación actual
     * y lo establece en las propiedades del componente.
     */
    getFromDraft() {
      if (this.conversationIdByRoute) {
        const draft = this.getDraft(this.conversationIdByRoute, this.replyType);
        this.message = draft?.message || '';
        this.attachedFiles = draft?.attachedFiles || [];
      }
    },

    /**
     * Elimina el borrador del mensaje guardado para la conversación actual del store de Vuex.
     */
    removeFromDraft() {
      this.$store.dispatch('removeDraft', {
        conversationId: this.conversationIdByRoute,
        replyType: this.replyType,
      });
    },
    handleKeyEvents(e) {
      const keyCode = buildHotKeys(e);
      if (keyCode === 'escape') {
        this.hideEmojiPicker();
        this.hideMentions();
      } else if (keyCode === 'meta+k') {
        const ninja = document.querySelector('ninja-keys');
        ninja.open();
        e.preventDefault();
      } else if (keyCode === 'enter' && this.isAValidEvent('enter')) {
        this.onSendReply();
        e.preventDefault();
      } else if (
        ['meta+enter', 'ctrl+enter'].includes(keyCode) &&
        this.isAValidEvent('cmd_enter')
      ) {
        this.onSendReply();
      }
    },
    isAValidEvent(selectedKey) {
      // Permitir el envío si hay archivos adjuntos, incluso si showMentions está activo
      const hasAttachmentsAndCanSend =
        this.hasAttachments && !this.isReplyButtonDisabled;

      return (
        !this.showUserMentions &&
        (!this.showMentions || hasAttachmentsAndCanSend) &&
        (!this.showCannedMenu || hasAttachmentsAndCanSend) &&
        !this.showVariablesMenu &&
        this.isFocused &&
        isEditorHotKeyEnabled(this.uiSettings, selectedKey)
      );
    },
    onPaste(e) {
      const data = e.clipboardData.files;
      if (!this.showRichContentEditor && data.length !== 0) {
        this.$refs.messageInput.$el.blur();
      }
      if (!data.length || !data[0]) {
        return;
      }
      data.forEach(file => {
        const { name, type, size } = file;
        this.onFileUpload({ name, type, size, file: file });
      });

      // Restaurar el foco al campo de mensaje después de procesar archivos adjuntos
      if (!this.showRichContentEditor && data.length !== 0) {
        this.$nextTick(() => {
          if (this.$refs.messageInput && this.$refs.messageInput.$el) {
            this.$refs.messageInput.$el.focus();
          }
        });
      }
    },
    toggleUserMention(currentMentionState) {
      this.showUserMentions = currentMentionState;
    },
    toggleCannedMenu(value) {
      this.showCannedMenu = value;
    },
    toggleVariablesMenu(value) {
      this.showVariablesMenu = value;
    },
    openWhatsappTemplateModal() {
      this.showWhatsAppTemplatesModal = true;
    },
    hideWhatsappTemplatesModal() {
      this.showWhatsAppTemplatesModal = false;
    },
    openMessengerTemplateModal() {
      this.showMessengerTemplatesModal = true;
    },
    hideMessengerTemplatesModal() {
      this.showMessengerTemplatesModal = false;
    },
    onClickSelfAssign() {
      const {
        account_id,
        availability_status,
        available_name,
        email,
        id,
        name,
        role,
        avatar_url,
      } = this.currentUser;
      const selfAssign = {
        account_id,
        availability_status,
        available_name,
        email,
        id,
        name,
        role,
        thumbnail: avatar_url,
      };
      this.assignedAgent = selfAssign;
    },
    confirmOnSendReply() {
      if (this.isReplyButtonDisabled) {
        return;
      }
      if (!this.showMentions) {
        let newMessage = this.message;
        if (this.isSignatureEnabledForInbox && this.messageSignature) {
          newMessage += '\n\n' + this.messageSignature;
        }

        const isOnWhatsApp =
          this.isATwilioWhatsAppChannel ||
          this.isAWhatsAppCloudChannel ||
          this.is360DialogWhatsAppChannel ||
          this.isAnEvolutionApiWhatsAppChannel;
        if (isOnWhatsApp && !this.isPrivate) {
          this.sendMessageAsMultipleMessages(newMessage);
        } else {
          const messagePayload = this.getMessagePayload(newMessage);
          this.sendMessage(messagePayload);
        }

        if (!this.isPrivate) {
          this.clearEmailField();
        }

        this.clearMessage();
        this.hideEmojiPicker();
        this.$emit('update:popoutReplyBox', false);
      }
    },
    sendMessageAsMultipleMessages(message) {
      const messages = this.getMessagePayloadForWhatsapp(message);
      if (
        !this.isAWhatsAppCloudChannel &&
        !this.isAnEvolutionApiWhatsAppChannel
      ) {
        messages.forEach(messagePayload => {
          this.sendMessage(messagePayload);
        });
      } else {
        messages.forEach(messagePayload => {
          if (
            messagePayload.message !== '' &&
            (!messagePayload?.files || messagePayload?.files?.length === 0)
          ) {
            this.sendMessage(messagePayload);
          } else {
            // envio solo el adjunto del mensaje
            const attachment = { ...messagePayload, message: '' };
            this.sendMessage(attachment);
            // envio solo el texto del mensaje
            if (messagePayload.message !== '') {
              const messageWithOutAttachment = { ...messagePayload };
              delete messageWithOutAttachment.files;
              this.sendMessage(messageWithOutAttachment);
            }
          }
        });
      }
    },
    async onSendReply() {
      const undefinedVariables = getUndefinedVariablesInMessage({
        message: this.message,
        variables: this.messageVariables,
      });
      if (undefinedVariables.length > 0) {
        const undefinedVariablesCount =
          undefinedVariables.length > 1 ? undefinedVariables.length : 1;
        this.undefinedVariableMessage = this.$t(
          'CONVERSATION.REPLYBOX.UNDEFINED_VARIABLES.MESSAGE',
          {
            undefinedVariablesCount,
            undefinedVariables: undefinedVariables.join(', '),
          }
        );

        const ok = await this.$refs.confirmDialog.showConfirmation();
        if (ok) {
          this.confirmOnSendReply();
        }
      } else {
        this.confirmOnSendReply();
      }
    },
    async sendMessage(messagePayload) {
      try {
        await this.$store.dispatch(
          'createPendingMessageAndSend',
          messagePayload
        );
        bus.$emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
        this.removeFromDraft();
        if (!messagePayload.private) {
          this.$nextTick(() => {
            const messageInput = this.$refs.messageInput;
            if (messageInput) {
              messageInput.focus();
            }
          });
        }
      } catch (error) {
        const errorMessage =
          error?.response?.data?.error || this.$t('CONVERSATION.MESSAGE_ERROR');
        this.showAlert(
          errorMessage.includes('CANNOT_RESEND_BECAUSE_MESSAGE_WINDOWS_ENDED')
            ? this.$t(
                'CONVERSATION.CANNOT_RESEND_BECAUSE_MESSAGE_WINDOWS_ENDED'
              )
            : errorMessage
        );
      }
    },
    async onSendWhatsAppReply(messagePayload) {
      this.sendMessage({
        conversationId: this.currentChat.id,
        ...messagePayload,
      });
      this.hideWhatsappTemplatesModal();
    },
    async onSendMessengerReply(messagePayload) {
      this.sendMessage({
        conversationId: this.currentChat.id,
        ...messagePayload,
      });
      this.hideMessengerTemplatesModal();
    },
    replaceText(message) {
      const updatedMessage = replaceVariablesInMessage({
        message,
        variables: this.messageVariables,
      });
      setTimeout(() => {
        this.$track(CONVERSATION_EVENTS.INSERTED_A_CANNED_RESPONSE);
        this.message = updatedMessage;
      }, 100);
    },
    setReplyMode(mode = REPLY_EDITOR_MODES.REPLY) {
      this.setReplyType(mode);
      if (this.showRichContentEditor) {
        if (this.isRecordingAudio) {
          this.toggleAudioRecorder();
        }
        return;
      }
      this.$nextTick(() => this.$refs.messageInput.focus());
    },
    clearEditorSelection() {
      this.updateEditorSelectionWith = '';
    },
    insertEmoji(emoji, selectionStart, selectionEnd) {
      const { message } = this;
      const newMessage =
        message.slice(0, selectionStart) +
        emoji +
        message.slice(selectionEnd, message.length);
      this.message = newMessage;
    },
    emojiOnClick(emoji) {
      if (this.showRichContentEditor) {
        this.updateEditorSelectionWith = emoji;
        this.onFocus();
      }
      if (!this.showRichContentEditor) {
        const { selectionStart, selectionEnd } = this.$refs.messageInput.$el;
        this.insertEmoji(emoji, selectionStart, selectionEnd);
      }
    },
    clearMessage() {
      this.message = '';
      this.attachedFiles = [];
      this.isRecordingAudio = false;
      this.resetReplyToMessage();
    },
    clearEmailField() {
      this.ccEmails = '';
      this.bccEmails = '';
    },
    toggleEmojiPicker() {
      this.showEmojiPicker = !this.showEmojiPicker;
    },
    toggleAudioRecorder() {
      this.isRecordingAudio = !this.isRecordingAudio;
      this.isRecorderAudioStopped = !this.isRecordingAudio;
      if (!this.isRecordingAudio) {
        this.clearMessage();
        this.clearEmailField();
      }
    },
    toggleAudioRecorderPlayPause() {
      if (this.isRecordingAudio) {
        if (!this.isRecorderAudioStopped) {
          this.isRecorderAudioStopped = true;
          this.$refs.audioRecorderInput.stopAudioRecording();
        } else if (this.isRecorderAudioStopped) {
          this.$refs.audioRecorderInput.playPause();
        }
      }
    },
    hideEmojiPicker() {
      if (this.showEmojiPicker) {
        this.toggleEmojiPicker();
      }
    },
    hideMentions() {
      this.showMentions = false;
    },
    onTypingOn() {
      this.toggleTyping('on');
    },
    onTypingOff() {
      this.toggleTyping('off');
    },
    onBlur() {
      this.isFocused = false;
      this.saveDraft(this.conversationIdByRoute, this.replyType);
    },
    onFocus() {
      this.isFocused = true;
    },
    onStateProgressRecorderChanged(duration) {
      this.recordingAudioDurationText = duration;
    },
    onStateRecorderChanged(state) {
      this.recordingAudioState = state;
      if (state && 'notallowederror'.includes(state)) {
        this.toggleAudioRecorder();
      }
    },
    onFinishRecorder(file) {
      return file && this.onFileUpload(file);
    },
    toggleTyping(status) {
      const conversationId = this.currentChat.id;
      const isPrivate = this.isPrivate;
      this.$store.dispatch('conversationTypingStatus/toggleTyping', {
        status,
        conversationId,
        isPrivate,
      });
    },
    onFileUpload(file) {
      if (this.globalConfig.directUploadsEnabled) {
        this.onDirectFileUpload(file);
      } else {
        this.onIndirectFileUpload(file);
      }
    },
    onDirectFileUpload(file) {
      if (!file) {
        return;
      }

      const MAXIMUM_SUPPORTED_FILE_UPLOAD_SIZE = this.isATwilioSMSChannel
        ? MAXIMUM_FILE_UPLOAD_SIZE_TWILIO_SMS_CHANNEL
        : getSizeFile(this.channelType, file);
      if (checkFileSizeLimit(file, MAXIMUM_SUPPORTED_FILE_UPLOAD_SIZE)) {
        const upload = new DirectUpload(
          file.file,
          `/api/v1/accounts/${this.accountId}/conversations/${this.currentChat.id}/direct_uploads`,
          {
            directUploadWillCreateBlobWithXHR: xhr => {
              xhr.setRequestHeader(
                'api_access_token',
                this.currentUser.access_token
              );
            },
          }
        );

        upload.create((error, blob) => {
          if (error) {
            this.showAlert(error);
          } else {
            this.attachFile({ file, blob });
          }
        });
      } else {
        const { name } = file;
        this.showAlert(
          this.$t('CONVERSATION.FILE_EXT_SIZE_LIMIT', {
            maximumFileSize: MAXIMUM_SUPPORTED_FILE_UPLOAD_SIZE,
            typeFile: name,
          })
        );
      }
    },
    // Mensaje del alert cuando se intenta adjuntar sin poder responder: con token
    // caducado se explica la causa (WhatsApp o Facebook) en lugar del genérico.
    cannotReplyAlertMessage() {
      if (this.inbox.reauthorization_required) {
        if (this.isAWhatsAppChannel) {
          return this.$t('CONVERSATION.CAN_REPLY_REAUTHORIZATION_REQUIRED');
        }
        if (this.isAFacebookInbox) {
          return this.$t(
            'CONVERSATION.CAN_REPLY_FACEBOOK_REAUTHORIZATION_REQUIRED'
          );
        }
      }
      return this.$t('CONVERSATION.CAN_REPLY_WHATSAPP_PLACEHOLDER');
    },
    onIndirectFileUpload(file) {
      if (!file) {
        return;
      }
      if (!this.canReply && !this.isOnPrivateNote) {
        // Con el token caducado no aplica sugerir plantillas: también fallarían con 401.
        this.showAlert(this.cannotReplyAlertMessage());
        return;
      }

      if (
        this.shouldShowReplyToMessage &&
        this.isAnEvolutionApiWhatsAppChannel
      ) {
        const { type } = file;
        if (type.includes('audio/')) {
          this.showAlert(this.$t('CONVERSATION.FILE_UNSUPPORTED'));
          return;
        }
      }

      const MAXIMUM_SUPPORTED_FILE_UPLOAD_SIZE = this.isATwilioSMSChannel
        ? MAXIMUM_FILE_UPLOAD_SIZE_TWILIO_SMS_CHANNEL
        : getSizeFile(this.channelType, file);
      if (checkFileSizeLimit(file, MAXIMUM_SUPPORTED_FILE_UPLOAD_SIZE)) {
        this.attachFile({ file });
      } else {
        const { name } = file;
        this.showAlert(
          this.$t('CONVERSATION.FILE_EXT_SIZE_LIMIT', {
            maximumFileSize: MAXIMUM_SUPPORTED_FILE_UPLOAD_SIZE,
            typeFile: name,
          })
        );
      }
    },
    attachFile({ blob, file }) {
      const reader = new FileReader();
      reader.readAsDataURL(file.file);
      reader.onloadend = () => {
        this.attachedFiles.push({
          currentChatId: this.currentChat.id,
          resource: blob || file,
          isPrivate: this.isPrivate,
          thumb: reader.result,
          blobSignedId: blob ? blob.signed_id : undefined,
        });
        this.saveDraft(this.conversationId, this.replyType);
      };
    },
    removeAttachment(itemIndex) {
      this.attachedFiles = this.attachedFiles.filter(
        (item, index) => itemIndex !== index
      );
      this.saveDraft(this.conversationId, this.replyType);
    },
    // Función para eliminar los archivos tipo audio
    removeAudiosAttachments() {
      this.attachedFiles = this.attachedFiles.filter(attachment => {
        const type =
          attachment.resource.file.content_type ||
          attachment.resource.file.type;
        return !type?.includes('audio');
      });
      this.saveDraft(this.conversationId, this.replyType);
    },
    /**
     * Agrega el ID del mensaje al que se está respondiendo al `payload`, si existe.
     *
     * @param {Object} payload - El objeto de datos al que se le debe agregar el ID del mensaje de respuesta.
     * @returns {Object} El `payload` modificado con el ID del mensaje de respuesta si aplica, o el mismo `payload` si no se está respondiendo a ningún mensaje.
     */
    setReplyToInPayload(payload) {
      if (this.inReplyTo?.id && !payload.private) {
        return {
          ...payload,
          contentAttributes: {
            ...payload.contentAttributes,
            in_reply_to: this.inReplyTo.id,
          },
        };
      }

      return payload;
    },
    getMessagePayloadForWhatsapp(message) {
      let multipleMessagePayload = [];
      let messagePayload = {
        conversationId: this.currentChat.id,
        message,
        private: false,
        sender: this.sender,
      };

      if (this.attachedFiles && this.attachedFiles.length) {
        const totalAttachments = this.attachedFiles.length;
        this.attachedFiles.forEach((attachment, index) => {
          if (totalAttachments > index + 1) {
            const attachedFile = this.globalConfig.directUploadsEnabled
              ? attachment.blobSignedId
              : attachment.resource.file;
            let attachmentPayload = {
              conversationId: this.currentChat.id,
              files: [attachedFile],
              private: false,
              message: '',
            };
            multipleMessagePayload.push(attachmentPayload);
          } else {
            // agrega el texto al ultimo mensaje
            messagePayload.files = [];
            if (this.globalConfig.directUploadsEnabled) {
              messagePayload.files.push(attachment.blobSignedId);
            } else {
              messagePayload.files.push(attachment.resource.file);
            }
          }
        });
      }

      messagePayload = this.setReplyToInPayload(messagePayload);
      multipleMessagePayload.push(messagePayload);
      return multipleMessagePayload;
    },
    getMessagePayload(message) {
      let messagePayload = {
        conversationId: this.currentChat.id,
        message,
        private: this.isPrivate,
        sender: this.sender,
      };
      messagePayload = this.setReplyToInPayload(messagePayload);

      if (this.attachedFiles && this.attachedFiles.length) {
        messagePayload.files = [];
        this.attachedFiles.forEach(attachment => {
          if (this.globalConfig.directUploadsEnabled) {
            messagePayload.files.push(attachment.blobSignedId);
          } else {
            messagePayload.files.push(attachment.resource.file);
          }
        });
      }

      if (this.ccEmails && !this.isOnPrivateNote) {
        messagePayload.ccEmails = this.ccEmails;
      }

      if (this.bccEmails && !this.isOnPrivateNote) {
        messagePayload.bccEmails = this.bccEmails;
      }

      return messagePayload;
    },
    setCcEmails(value) {
      this.bccEmails = value.bccEmails;
      this.ccEmails = value.ccEmails;
    },
    setCCEmailFromLastChat() {
      if (this.lastEmail) {
        const {
          content_attributes: { email: emailAttributes = {} },
        } = this.lastEmail;
        const cc = emailAttributes.cc || [];
        const bcc = emailAttributes.bcc || [];
        this.ccEmails = cc.join(', ');
        this.bccEmails = bcc.join(', ');
      }
    },
    /**
     * Restaura la referencia de respuesta desde localStorage al (re)montar el componente
     * (p. ej. al volver a la pestaña de mensajes en ConversationBox.vue), y se reintenta
     * desde el watcher de `currentChat.messages.length` por si los mensajes aún no habían
     * cargado al montar. No hace nada si ya hay una cita resuelta o si no hay conversación activa.
     */
    restoreReplyToMessage() {
      if (!this.conversationId || this.inReplyTo?.id) {
        return;
      }
      const replyToMessageId = LocalStorage.getFromJsonStore(
        LOCAL_STORAGE_KEYS.MESSAGE_REPLY_TO,
        this.conversationId
      );
      if (replyToMessageId) {
        this.fetchAndSetReplyTo();
      }
    },
    /**
     * Recupera el mensaje al que se está respondiendo desde el almacenamiento local
     * y lo asigna a la propiedad `inReplyTo`. Si hay un id guardado pero el mensaje
     * aún no está en currentChat.messages (carga en curso), no hace nada y deja que
     * el watcher de messages.length reintente, para no robar el foco en cada mensaje nuevo.
     */
    fetchAndSetReplyTo() {
      const replyStorageKey = LOCAL_STORAGE_KEYS.MESSAGE_REPLY_TO;
      const replyToMessageId = LocalStorage.getFromJsonStore(
        replyStorageKey,
        this.conversationId
      );

      if (!replyToMessageId) {
        this.inReplyTo = undefined;
        return;
      }

      const found = this.currentChat?.messages?.find(
        message => message.id === replyToMessageId
      );
      if (!found) {
        return;
      }

      this.inReplyTo = found;
      if (!this.isPrivate) {
        this.$nextTick(() => {
          const messageInput = this.$refs.messageInput;
          if (messageInput) {
            messageInput.focus();
          }
        });
      }
    },

    /**
     * Elimina la información sobre el mensaje al que se está respondiendo del almacenamiento local,
     * emite un evento para alternar la visibilidad del mensaje de respuesta y enfoca el campo de entrada.
     *
     * @returns {void} No devuelve ningún valor.
     */
    resetReplyToMessage() {
      const replyStorageKey = LOCAL_STORAGE_KEYS.MESSAGE_REPLY_TO;
      LocalStorage.deleteFromJsonStore(replyStorageKey, this.conversationId);
      bus.$emit(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE);
    },
  },
};
</script>

<style lang="scss" scoped>
.send-button {
  margin-bottom: 0;
}

.message-signature-wrap {
  margin: 0 var(--space-normal);
  padding: var(--space-small);
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  border: 1px dashed var(--s-100);
  border-radius: var(--border-radius-small);
  max-height: 8vh;
  overflow: auto;

  &:hover {
    background: var(--s-25);
  }
}

.message-signature {
  width: fit-content;
  margin: 0;
}

.attachment-preview-box {
  padding: 0 var(--space-normal);
  background: transparent;
}

.reply-box {
  border-top: 1px solid var(--color-border);
  background: white;

  &.is-private {
    background: var(--conversa2-orange-50-color);
  }
}
.send-button {
  margin-bottom: 0;
}

.reply-box__top {
  position: relative;
  padding: 0 var(--space-normal);
  border-top: 1px solid var(--color-border);
  margin-top: -1px;
}

.emoji-dialog {
  top: unset;
  bottom: var(--space-normal);
  left: -320px;
  right: unset;

  &::before {
    right: var(--space-minus-normal);
    bottom: var(--space-small);
    transform: rotate(270deg);
    filter: drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.08));
  }
}
.emoji-dialog--expanded {
  left: unset;
  bottom: var(--space-jumbo);
  position: absolute;
  z-index: var(--z-index-normal);

  &::before {
    transform: rotate(0deg);
    left: var(--space-smaller);
    bottom: var(--space-minus-small);
  }
}
.message-signature {
  margin-bottom: 0;

  ::v-deep p:last-child {
    margin-bottom: 0;
  }
}

.normal-editor__canned-box {
  width: calc(100% - 2 * var(--space-normal));
  left: var(--space-normal);
}
</style>
